import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Button, Table, Form, Card, Badge } from "react-bootstrap";
import Pagination from "rc-pagination";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import Layout from "../../layout/Layout";
import StatusSwitch from "../../Component/theme-management/StatusSwitch";
import TableSkeleton from "../../Component/theme-management/TableSkeleton";
import EmptyState from "../../Component/theme-management/EmptyState";
import ErrorState from "../../Component/theme-management/ErrorState";
import { widgetsApi, themesApi, isAuthError } from "../../helpers/tmClient";
import {
  WIDGET_SCREEN_OPTIONS,
  WIDGET_CATEGORY_OPTIONS,
  PAGE_SIZE_OPTIONS,
  labelOf,
} from "../../helpers/tmConstants";

/**
 * ThemeWidgets — listing page. Filters by theme, category, screen
 * type, premium and status; shows live variant counts.
 */
const ThemeWidgets = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({
    search: "", theme_id: "", category_name: "", widget_screen: "", premium: "", status: "",
  });
  // Per-row "updating" flags — any in-flight action (status / delete)
  // disables that row's controls so it can't be double-triggered.
  const [rowBusy, setRowBusy] = useState({});
  const debounceRef = useRef(null);
  const lastArgsRef = useRef({});

  const fetchData = useCallback(
    async (overrides = {}) => {
      lastArgsRef.current = overrides;
      setLoading(true);
      setLoadError(false);
      try {
        const params = { page: meta.page, limit: meta.limit, ...filters, ...overrides };
        const res = await widgetsApi.list(params);
        if (isAuthError(res)) return navigate("/");
        setData(res.data.data || []);
        if (res.data.meta) setMeta((m) => ({ ...m, ...res.data.meta }));
      } catch {
        setLoadError(true);
        toast.error("Failed to load widgets", { position: "bottom-right", autoClose: 1000 });
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meta.page, meta.limit, filters, navigate]
  );

  const retry = () => fetchData(lastArgsRef.current);

  useEffect(() => {
    (async () => {
      try {
        const res = await themesApi.options();
        if (!isAuthError(res)) setThemes(res.data.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [meta.page, meta.limit]);

  const onFilter = (e) => {
    const { name, value } = e.target;
    const next = { ...filters, [name]: value };
    setFilters(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setMeta((m) => ({ ...m, page: 1 }));
      fetchData({ ...next, page: 1 });
    }, 400);
  };

  const toggle = async (id, value) => {
    setRowBusy((b) => ({ ...b, [id]: true }));
    try {
      await widgetsApi.changeStatus(id, value);
      setData((d) => d.map((r) => (r._id === id ? { ...r, status: value } : r)));
      toast.success("Status updated", { position: "bottom-right", autoClose: 500 });
    } catch {
      toast.error("Update failed", { position: "bottom-right", autoClose: 800 });
    } finally {
      setRowBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const remove = (id) => {
    const swal = Swal.mixin({ customClass: { confirmButton: "btn btn-success", cancelButton: "btn btn-danger me-3" }, buttonsStyling: false });
    swal.fire({
      title: "Are you sure?", text: "Deleting a widget removes its variants too!", icon: "warning",
      showCancelButton: true, confirmButtonText: "Yes, delete it!", cancelButtonText: "No, cancel!", reverseButtons: true,
    }).then(async (r) => {
      if (r.isConfirmed) {
        setRowBusy((b) => ({ ...b, [id]: true }));
        try { await widgetsApi.remove(id); toast.success("Widget deleted", { position: "bottom-right", autoClose: 700 }); fetchData(); }
        catch {
          toast.error("Delete failed", { position: "bottom-right", autoClose: 800 });
          setRowBusy((b) => ({ ...b, [id]: false }));
        }
      }
    });
  };

  const PrevNextArrow = (c, type, el) => {
    if (type === "prev") return <button className="paggination-btn">Previous</button>;
    if (type === "next") return <button className="paggination-btn">Next</button>;
    return el;
  };

  /** Drag & drop reorder — recalculates sort_order and persists it. */
  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(data);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setData(reordered);
    const offset = (meta.page - 1) * meta.limit;
    const items = reordered.map((row, idx) => ({ id: row._id, sort_order: offset + idx + 1 }));
    try {
      await widgetsApi.updateSort(items);
      toast.success("Order updated", { position: "bottom-right", autoClose: 700 });
      fetchData();
    } catch {
      toast.error("Failed to save order", { position: "bottom-right", autoClose: 900 });
      fetchData();
    }
  };

  return (
    <Layout sidebar={true}>
      <div className="page-heading">
        <h3 className="my-1">Theme Widgets</h3>
        <Row className="align-items-center">
          <Col md="auto" xs={12}>
            <Form.Control type="text" name="search" placeholder="Search widget name" className="my-1" value={filters.search} onChange={onFilter} />
          </Col>
          <Col md="auto" xs={12}>
            <Form.Select className="my-1" name="theme_id" onChange={onFilter}>
              <option value="">All Themes</option>
              {themes.map((t) => (<option key={t._id} value={t._id}>{t.name}</option>))}
            </Form.Select>
          </Col>
          <Col md="auto" xs={12}>
            <Form.Select className="my-1" name="widget_screen" onChange={onFilter}>
              <option value="">All Screens</option>
              {WIDGET_SCREEN_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </Form.Select>
          </Col>
          <Col md="auto" xs={12}>
            <Form.Select className="my-1" name="category_name" onChange={onFilter}>
              <option value="">All Categories</option>
              {WIDGET_CATEGORY_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </Form.Select>
          </Col>
          <Col md="auto" xs={12}>
            <Form.Select className="my-1" name="status" onChange={onFilter}>
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Form.Select>
          </Col>
          <Col md="auto" xs={6}>
            <Form.Select className="my-1" value={meta.limit} onChange={(e) => setMeta((m) => ({ ...m, page: 1, limit: Number(e.target.value) }))}>
              {PAGE_SIZE_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
            </Form.Select>
          </Col>
          <Col md="auto" xs={12}>
            <Link to="/theme-widgets/add"><Button variant="primary" className="my-1">Add New</Button></Link>
          </Col>
        </Row>
      </div>

      <div className="page-content">
        <Card>
          <Card.Body>
            <DragDropContext onDragEnd={onDragEnd}>
            <Table bordered responsive>
              <thead>
                <tr>
                  <th width="5%" className="text-center">No</th>
                  <th>Widget Name</th>
                  <th>Theme</th>
                  <th className="text-center">Screen</th>
                  <th>Category</th>
                  <th className="text-center">Premium</th>
                  <th className="text-center">Variants</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              {loading ? (
                <tbody><TableSkeleton rows={6} cols={9} /></tbody>
              ) : loadError ? (
                <tbody><ErrorState colSpan={9} message="Failed to load widgets." onRetry={retry} /></tbody>
              ) : data.length === 0 ? (
                <tbody><EmptyState colSpan={9} message="No widgets found" icon="bxs-widget" /></tbody>
              ) : (
                <Droppable droppableId="widgets-droppable">
                  {(provider) => (
                    <tbody ref={provider.innerRef} {...provider.droppableProps}>
                      {data.map((val, id) => (
                        <Draggable key={val._id} draggableId={val._id} index={id}>
                          {(prov) => (
                    <tr key={val._id} ref={prov.innerRef} {...prov.draggableProps}>
                      <td className="text-center">
                        <i className="bx bx-menu me-1 text-muted" title="Drag to reorder" style={{ cursor: "grab" }} {...prov.dragHandleProps}></i>
                        {(meta.page - 1) * meta.limit + id + 1}
                      </td>
                      <td>{val.widget_name}</td>
                      <td>{val.theme?.name || <Badge bg="secondary">—</Badge>}</td>
                      <td className="text-center text-capitalize">
                        <Badge bg={val.widget_screen === "home" ? "info" : "dark"}>
                          {labelOf(WIDGET_SCREEN_OPTIONS, val.widget_screen)}
                        </Badge>
                      </td>
                      <td>{val.category_name || "-"}</td>
                      <td className="text-center">
                        {val.premium ? <Badge bg="warning" text="dark">Premium</Badge> : <Badge bg="secondary">Free</Badge>}
                      </td>
                      <td className="text-center"><Badge bg="primary">{val.variant_count ?? (val.sizes ? val.sizes.length : 0)}</Badge></td>
                      <td className="text-center">
                        <StatusSwitch checked={val.status === true} disabled={!!rowBusy[val._id]} onChange={(e) => toggle(val._id, e)} />
                      </td>
                      <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                        <Link to={`/theme-widgets/edit/${val._id}`}>
                          <Button variant="outline-primary" size="sm" className="me-2 btn-icon" disabled={!!rowBusy[val._id]}><i className="bx bx-edit"></i></Button>
                        </Link>
                        <Button variant="outline-danger" size="sm" className="btn-icon" disabled={!!rowBusy[val._id]} onClick={() => remove(val._id)}>
                          {rowBusy[val._id] ? (
                            <span className="spinner-border spinner-border-sm" role="status"></span>
                          ) : (
                            <i className="bx bx-trash-alt"></i>
                          )}
                        </Button>
                      </td>
                    </tr>
                          )}
                        </Draggable>
                      ))}
                      {provider.placeholder}
                    </tbody>
                  )}
                </Droppable>
              )}
            </Table>
            </DragDropContext>

            {meta.total > meta.limit && (
              <div className="pagination-custom" style={loading ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
                <Pagination
                  className="pagination-data"
                  onChange={(page) => setMeta((m) => ({ ...m, page }))}
                  total={meta.total} current={meta.page} pageSize={meta.limit}
                  showSizeChanger={false} itemRender={PrevNextArrow} showTitle={false}
                />
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </Layout>
  );
};

export default ThemeWidgets;
